import React, { SyntheticEvent, useCallback } from 'react';
import { debounce } from 'lodash';

import { Alert, AlertDescription } from '@zapier/design-system';
import { useLink } from '@zapier/common-hooks';

import ClickableTable from 'app/developer-v3/components/ClickableTable';
import ActionsDropdownMenu from 'app/developer-v3/components/ActionsDropdownMenu';
import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import SortControls from 'app/developer-v3/components/SortControls';
import { NEW_SERVICE_FIELD } from 'app/developer-v3/constants';
import { useCurrentVersionReadOnlyReason } from 'app/developer-v3/hooks';
import {
  getInputFields,
  getTriggerUrl,
  isLastDescendantNodeClicked,
  reorderList,
} from 'app/developer-v3/utils';

import type { FieldOrFunctionSchema } from 'app/developer-v3/platformSchema/field';
import type { TriggerParams } from 'app/developer-v3/types';
import type { TriggerSchema } from 'app/developer-v3/platformSchema/trigger';

type Props = {
  params: TriggerParams;
  trigger: TriggerSchema;
  deleteInputField: (fieldIndex: number) => void;
  updateInputFields: (fields: FieldOrFunctionSchema[]) => void;
  isSaving: boolean;
  saveTriggerChanges: () => void;
};

const TriggerInputsList = (props: Props) => {
  const {
    deleteInputField,
    isSaving,
    params,
    trigger,
    saveTriggerChanges,
    updateInputFields,
  } = props;

  const { routeTo } = useLink();
  const isReadOnly = !!useCurrentVersionReadOnlyReason();

  const reorderFields = (inputIndex: number, direction: number) => {
    const inputs = getInputFields(trigger);
    const newIndex = inputIndex + direction;
    const updatedFields = reorderList(inputs, inputIndex, newIndex);

    updateInputFields(updatedFields);
  };

  const getCellModifiers = useCallback(
    (index: number) => ({
      darken: index < 2,
      wrap: index === 1,
    }),
    []
  );

  const onClickRow = useCallback(
    (event: SyntheticEvent<HTMLElement>, index: number) => {
      if (!isLastDescendantNodeClicked(event)) {
        routeTo(`${getTriggerUrl(params)}/input/${index}`);
      }
    },
    [params, routeTo]
  );

  const debouncedSaveTriggerChanges = useCallback(
    debounce(() => {
      saveTriggerChanges();
    }, 2000),
    [saveTriggerChanges]
  );

  const inputFields = getInputFields(trigger);

  return (
    <ManagerLayout
      buttonRoute={`${getTriggerUrl(params)}/input/${NEW_SERVICE_FIELD}`}
      buttonText="Add User Input Field"
      isButtonDisabled={isReadOnly}
      shouldLimitWidth={true}
    >
      {inputFields.length === 0 ? (
        <Alert
          status="info"
          title="No input fields have been added to this Trigger yet"
        >
          <AlertDescription>
            Zapier Triggers may optionally include a form where users enter
            plain text to pass to your API. Add each field needed to create this
            Trigger item in your integration.
          </AlertDescription>
        </Alert>
      ) : (
        <ClickableTable
          body={inputFields.map((field, index, all) => {
            const label = String(field.label || '');
            const key = String(field.key || '');

            return [
              { value: '', element: label },
              { value: '', element: key },
              {
                value: '',
                element: (
                  <SortControls
                    isDisabledMoveDown={isSaving || index === all.length - 1}
                    isDisabledMoveUp={isSaving || index === 0}
                    onMoveDown={() => {
                      reorderFields(index, 1);
                      debouncedSaveTriggerChanges();
                    }}
                    onMoveUp={() => {
                      reorderFields(index, -1);
                      debouncedSaveTriggerChanges();
                    }}
                  />
                ),
              },
              {
                value: '',
                element: !isReadOnly && (
                  <ActionsDropdownMenu
                    items={[
                      {
                        label: 'Edit',
                        onChoose: () =>
                          routeTo(`${getTriggerUrl(params)}/input/${index}`),
                        value: 'edit',
                      },
                      {
                        isConfirmationRequired: true,
                        label: 'Delete',
                        modalTextContent: `Are you sure you want to delete the input field with key: ${key}`,
                        modalTitle: 'Delete input field',
                        onChoose: () => deleteInputField(index),
                        value: 'delete',
                      },
                    ]}
                  />
                ),
              },
            ];
          })}
          getCellModifiers={getCellModifiers}
          header={[
            { title: 'Label', hasSort: false },
            { title: 'Key', hasSort: false },
            { title: 'Sort', hasSort: false },
            { title: '', hasSort: false },
          ]}
          onClickRow={onClickRow}
          rowAriaLabel="Click to edit this input field"
        />
      )}
    </ManagerLayout>
  );
};

export default TriggerInputsList;
